import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Link2, Trash2, Plus, X, Percent, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EstimateurConfigDialog({ estimateur, isOpen, onClose, onUpdate }) {
  const { getAuthHeader } = useAuth();
  const [config, setConfig] = useState(estimateur?.config || {});
  const [contributions, setContributions] = useState([]);
  const [linkedNomenclatures, setLinkedNomenclatures] = useState([]);
  const [availableNomenclatures, setAvailableNomenclatures] = useState([]);
  const [selectedNomenclatures, setSelectedNomenclatures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && estimateur) {
      fetchLinkedNomenclatures();
      fetchAvailableNomenclatures();
      setConfig(estimateur.config || {});
      // Charger les contributions depuis la config
      setContributions(estimateur.config?.contributions || []);
    }
  }, [isOpen, estimateur]);

  const fetchLinkedNomenclatures = async () => {
    try {
      const response = await axios.get(
        `${API}/estimateurs/${estimateur.id}/nomenclatures?linked_only=true`,
        { headers: getAuthHeader() }
      );
      setLinkedNomenclatures(response.data);
    } catch {
    }
  };

  const fetchAvailableNomenclatures = async () => {
    try {
      const response = await axios.get(
        `${API}/estimateurs/${estimateur.id}/nomenclatures?linked_only=false`,
        { headers: getAuthHeader() }
      );
      setAvailableNomenclatures(response.data);
    } catch {
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/estimateurs/${estimateur.id}/config`,
        config,
        { headers: getAuthHeader() }
      );
      toast.success('Configuration enregistrée');
      onUpdate();
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkNomenclatures = async () => {
    if (selectedNomenclatures.length === 0) {
      toast.error('Aucune activité sélectionnée');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/estimateurs/${estimateur.id}/nomenclatures/link`,
        {
          estimateur_id: estimateur.id,
          nomenclature_ids: selectedNomenclatures
        },
        { headers: getAuthHeader() }
      );
      toast.success(`${selectedNomenclatures.length} nomenclature(s) liée(s)`);
      setSelectedNomenclatures([]);
      fetchLinkedNomenclatures();
      fetchAvailableNomenclatures();
      onUpdate();
    } catch {
      toast.error('Erreur lors de la liaison');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkNomenclature = async (nomenclatureId) => {
    if (!window.confirm('Délier cette nomenclature ?')) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/estimateurs/${estimateur.id}/nomenclatures/unlink`,
        {
          estimateur_id: estimateur.id,
          nomenclature_ids: [nomenclatureId]
        },
        { headers: getAuthHeader() }
      );
      toast.success('Nomenclature déliée');
      fetchLinkedNomenclatures();
      fetchAvailableNomenclatures();
      onUpdate();
    } catch {
      toast.error('Erreur lors de la suppression du lien');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig({ ...config, [key]: value });
  };

  const addConfigField = () => {
    const key = prompt('Nom du paramètre :');
    if (key && key.trim()) {
      setConfig({ ...config, [key.trim()]: '' });
    }
  };

  const removeConfigField = (key) => {
    const newConfig = { ...config };
    delete newConfig[key];
    setConfig(newConfig);
  };

  // Fonctions pour gérer les contributions
  const addContribution = () => {
    setContributions([
      ...contributions,
      {
        id: Date.now().toString(),
        name: '',
        type: 'pourcentage',
        taux: 0,
        montant_fixe: 0,
        is_active: true
      }
    ]);
  };

  const updateContribution = (id, field, value) => {
    setContributions(contributions.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const removeContribution = (id) => {
    setContributions(contributions.filter(c => c.id !== id));
  };

  const handleSaveContributions = async () => {
    setLoading(true);
    try {
      // Valider les contributions
      for (const contrib of contributions) {
        if (!contrib.name.trim()) {
          toast.error('Veuillez renseigner le nom de toutes les contributions');
          setLoading(false);
          return;
        }
      }
      
      // Sauvegarder avec les contributions dans la config
      const updatedConfig = {
        ...config,
        contributions: contributions
      };
      
      await axios.put(
        `${API}/estimateurs/${estimateur.id}`,
        { config: updatedConfig },
        { headers: getAuthHeader() }
      );
      
      setConfig(updatedConfig);
      toast.success('Contributions et redevances enregistrées');
      onUpdate?.();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const toggleNomenclatureSelection = (nomenclatureId) => {
    setSelectedNomenclatures(prev =>
      prev.includes(nomenclatureId)
        ? prev.filter(id => id !== nomenclatureId)
        : [...prev, nomenclatureId]
    );
  };

  // Sélectionner toutes les nomenclatures filtrées
  const selectAllNomenclatures = () => {
    const allFilteredIds = filteredAvailableNomenclatures.map(nom => nom.id);
    setSelectedNomenclatures(prev => {
      const newSelection = [...prev];
      allFilteredIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  // Désélectionner toutes les nomenclatures
  const deselectAllNomenclatures = () => {
    setSelectedNomenclatures([]);
  };

  const filteredAvailableNomenclatures = availableNomenclatures.filter(nom =>
    !linkedNomenclatures.find(linked => linked.id === nom.id) &&
    (nom.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     nom.code?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!estimateur) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuration : {estimateur.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="config" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <TabsTrigger value="config">Paramètres</TabsTrigger>
            <TabsTrigger value="contributions">Contributions</TabsTrigger>
            <TabsTrigger value="nomenclatures">
              Activités ({linkedNomenclatures.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="flex-1 overflow-y-auto space-y-4">
            <div className="space-y-4 p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Configuration personnalisée</h3>
                <Button onClick={addConfigField} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un paramètre
                </Button>
              </div>

              {Object.keys(config).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Aucun paramètre configuré</p>
                  <p className="text-sm mt-2">Cliquez sur "Ajouter un paramètre" pour commencer</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(config).map(([key, value]) => (
                    <div key={key} className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">Paramètre</Label>
                          <Input
                            value={key}
                            disabled
                            className="bg-gray-50"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Valeur</Label>
                          <Input
                            value={value}
                            onChange={(e) => handleConfigChange(key, e.target.value)}
                            placeholder="Valeur..."
                          />
                        </div>
                      </div>
                      <Button
                        onClick={() => removeConfigField(key)}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 mt-6"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button onClick={onClose} variant="outline">
                  Annuler
                </Button>
                <Button onClick={handleSaveConfig} disabled={loading}>
                  Enregistrer la configuration
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Onglet Contributions et Redevances */}
          <TabsContent value="contributions" className="flex-1 overflow-y-auto">
            <div className="space-y-4 p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">Contributions et Redevances</h3>
                  <p className="text-sm text-gray-500">Configurez les taxes appliquées aux calculs</p>
                </div>
                <Button onClick={addContribution} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter
                </Button>
              </div>

              {contributions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <Percent className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Aucune contribution configurée</p>
                  <p className="text-sm mt-2">Les valeurs par défaut seront utilisées :</p>
                  <ul className="text-sm mt-2 space-y-1">
                    <li>• Taxe fiscale : 9%</li>
                    <li>• Contribution terrorisme : 5,90€ (fixe)</li>
                    <li>• Contribution FOGEDOM : 9%</li>
                    <li>• Frais de gestion : 8,5%</li>
                  </ul>
                </div>
              ) : (
                <div className="space-y-4">
                  {contributions.map((contrib, index) => (
                    <div key={contrib.id} className="border rounded-lg p-4 bg-white shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                          <Switch
                            checked={contrib.is_active}
                            onCheckedChange={(checked) => updateContribution(contrib.id, 'is_active', checked)}
                          />
                          <span className={`text-xs ${contrib.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                            {contrib.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        <Button
                          onClick={() => removeContribution(contrib.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Nom de la contribution</Label>
                          <Input
                            value={contrib.name}
                            onChange={(e) => updateContribution(contrib.id, 'name', e.target.value)}
                            placeholder="Ex: Taxe fiscale"
                          />
                        </div>

                        <div>
                          <Label className="text-xs text-gray-500">Type de calcul</Label>
                          <Select
                            value={contrib.type}
                            onValueChange={(value) => updateContribution(contrib.id, 'type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pourcentage">
                                <div className="flex items-center gap-2">
                                  <Percent className="h-4 w-4" />
                                  Pourcentage
                                </div>
                              </SelectItem>
                              <SelectItem value="fixe">
                                <div className="flex items-center gap-2">
                                  <DollarSign className="h-4 w-4" />
                                  Montant fixe
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          {contrib.type === 'pourcentage' ? (
                            <>
                              <Label className="text-xs text-gray-500">Taux (%)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={contrib.taux}
                                onChange={(e) => updateContribution(contrib.id, 'taux', parseFloat(e.target.value) || 0)}
                                placeholder="Ex: 9"
                              />
                            </>
                          ) : (
                            <>
                              <Label className="text-xs text-gray-500">Montant fixe (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={contrib.montant_fixe}
                                onChange={(e) => updateContribution(contrib.id, 'montant_fixe', parseFloat(e.target.value) || 0)}
                                placeholder="Ex: 5.90"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button onClick={onClose} variant="outline">
                  Annuler
                </Button>
                <Button onClick={handleSaveContributions} disabled={loading}>
                  Enregistrer les contributions
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="nomenclatures" className="flex-1 overflow-y-auto">
            <div className="space-y-4 p-4">
              {/* Nomenclatures déjà liées */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Nomenclatures liées</h3>
                {linkedNomenclatures.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    Aucune nomenclature liée
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                    {linkedNomenclatures.map(nom => (
                      <div
                        key={nom.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{nom.name}</p>
                          {nom.code && (
                            <p className="text-xs text-gray-500">{nom.code}</p>
                          )}
                        </div>
                        <Button
                          onClick={() => handleUnlinkNomenclature(nom.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Ajouter des nomenclatures */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Ajouter des nomenclatures</h3>
                
                <div className="mb-3">
                  <Input
                    placeholder="Rechercher par nom ou code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Boutons Tout sélectionner / Tout désélectionner */}
                <div className="flex gap-2 mb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllNomenclatures}
                    disabled={filteredAvailableNomenclatures.length === 0}
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Tout sélectionner ({filteredAvailableNomenclatures.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAllNomenclatures}
                    disabled={selectedNomenclatures.length === 0}
                    className="flex-1"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Tout désélectionner
                  </Button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                  {filteredAvailableNomenclatures.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      Aucune nomenclature disponible
                    </div>
                  ) : (
                    filteredAvailableNomenclatures.map(nom => (
                      <div
                        key={nom.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={() => toggleNomenclatureSelection(nom.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedNomenclatures.includes(nom.id)}
                          onChange={() => toggleNomenclatureSelection(nom.id)}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{nom.name}</p>
                          <div className="flex gap-2 items-center">
                            {nom.code && (
                              <Badge className="bg-slate-700 text-white text-xs font-semibold">
                                {nom.code}
                              </Badge>
                            )}
                            <Badge className="bg-blue-700 text-white text-xs font-semibold">
                              {nom.category}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {selectedNomenclatures.length > 0 && (
                  <div className="flex justify-between items-center mt-3 p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium">
                      {selectedNomenclatures.length} activité(s) sélectionnée(s)
                    </span>
                    <Button
                      onClick={handleLinkNomenclatures}
                      size="sm"
                      disabled={loading}
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Lier les nomenclatures
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
